import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const VKB_LOGO_URL = "https://customer-assets.emergentagent.com/job_ingenieur-agency/artifacts/7oos7e3e_VKBayern_logo.svg.png";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Startseite" },
    { path: "/leistungen", label: "Leistungen" },
    { path: "/vorteile", label: "Vorteile" },
    { path: "/ueber-mich", label: "Über mich" },
    { path: "/kontakt", label: "Kontakt" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
              <img
                src={VKB_LOGO_URL}
                alt="Versicherungskammer Bayern"
                className="h-14 md:h-16 w-auto"
              />
              <div className="hidden sm:block border-l border-slate-300 pl-3">
                <span className="text-xs font-medium text-slate-500 block leading-tight">
                  Fachagentur
                </span>
                <span className="text-sm font-semibold text-[#003B70] leading-tight">
                  Architekten & Ingenieure
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                  className={`nav-link text-sm font-medium ${
                    isActive(link.path)
                      ? "text-[#003B70]"
                      : "text-slate-600"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:+493033937950"
                className="flex items-center gap-2 text-slate-600 hover:text-[#003B70] transition-colors"
                data-testid="header-phone"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm font-medium hidden xl:inline">+49 30 33937950</span>
              </a>
              <Link to="/kontakt">
                <Button
                  data-testid="header-cta-button"
                  className="bg-[#003B70] hover:bg-[#002855] text-white px-6"
                >
                  Jetzt Beratung anfragen
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:text-[#003B70]"
              aria-label="Menü öffnen"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 bg-white z-40 pt-20 transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
              className={`text-lg font-medium py-3 border-b border-slate-100 ${
                isActive(link.path)
                  ? "text-[#003B70]"
                  : "text-slate-600"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-6 space-y-4">
            <a
              href="tel:+493033937950"
              className="flex items-center gap-3 text-slate-600 py-3"
            >
              <Phone className="h-5 w-5" />
              <span className="font-medium">+49 30 33937950</span>
            </a>
            <Link to="/kontakt">
              <Button
                data-testid="mobile-cta-button"
                className="w-full bg-[#003B70] hover:bg-[#002855] text-white"
              >
                Jetzt Beratung anfragen
              </Button>
            </Link>
          </div>
        </nav>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-20" />
    </>
  );
}
